import React, { FC, useState, useContext, useEffect, useRef } from 'react'
import {observer} from 'mobx-react'
import _debug from 'debug'

import { t } from 'ttag'
import { displayError, displayInfo } from '../utils/Errors'
import PortionNameEditor from './PortionEditor'
import { Root } from '../../models3/Root'
import { Portion } from '../../models3/ProjectModels'
import './Portion.css'
import { VideoCache } from '../../models3/VideoCache'
import { RootConsumer } from '../app/RootContext'
import { SortableHandle, SortableElement } from 'react-sortable-hoc'
import { confirmDelete } from '../utils/ConfirmDelete'
import { PortionNotificationList } from '../notifications/Notifications'
import { TrlNotificationList } from "../notifications/TrlNotificationList"
import { useToggle } from '../utils/Hooks'
import { PortionsListTRLContext, ProjectTRLIssue } from '../utils/TRL'
import { TRLPublish } from './TRLPublish'
import { TRLIssueEditor } from './TRLIssueEditor'
import { TitlesEditor } from './TitlesEditor'
import { useUserCanDelete } from '../utils/useUserCanDelete'
import { VideoCacheDownloader } from '../../models3/VideoCacheDownloader'
import { requestPersistentStorage } from '../utils/navigatorStorage'
import { updatePortionDownloadedAt } from '../../models3/VideoCache.utils'

const log = _debug('sltt:PortionView')

interface ISortableElement {
    rt: Root,
    portion: Portion,
}

const PortionListItem = SortableElement(
    function (props: ISortableElement) {
        let { rt, portion } = props
        const [toggleBox, setToggleBox] = useToggle('')
        let { iAmInterpreter, useMobileLayout, username, iAmAdmin } = rt
        const trlContext = useContext(PortionsListTRLContext)
        const { projectTrlIssue } = trlContext

        function onDelete() { 
            log('onDelete')
            let { project } = rt

            confirmDeletion( () => {               
                project.removePortion(portion._id).then(() => {
                    // If the deleted portion was the selected one, switch to the first portion in the list
                    if (rt.portion?._id === portion._id) {
                        const remainingPortions = project.portions.filter(p => p._id !== portion._id)
                        rt.setPortion(remainingPortions[0] ?? null)
                        log(`Selected portion was deleted, so changed to first remaining portion.`)
                    }
                }).catch(displayError)
            })
        }

        function confirmDeletion(doDeletion: () => void) {
            // No need to confirm, if no video has been recorded yet
            if (!portion.videod()) { doDeletion(); return }

            confirmDelete({
                title: /* translator: important */ t`Delete Video!?`,    
                message: /* translator: important */ t`Video has already been recorded for this portion.`, 
                confirmLabel: /* translator: important */ t`Delete video!`,    
                cancelLabel: /* translator: important */ t`Keep video.`,    
                onConfirm: doDeletion, 
                onCancel: () => {}, 
            })
        }

        const className = toggleBox && 'portion-toggle-box-open'|| ''
        let assigned = portion.passages.some(passage => passage.assignee === username && passage.task !== 'Finished')
        const hasProjectTRLIssue = projectTrlIssue !== ProjectTRLIssue.None
        const trlToggleBox = hasProjectTRLIssue && ToggleBox.TRLIssue || ToggleBox.TRLPublish
        const { project } = rt
        return (
            <div className={className}>
                <div className={`portion-portion ${assigned ? 'passage-assigned' : ''}`}>
                    { iAmInterpreter && !useMobileLayout && <DragHandle /> }
                    <PortionView
                        portion={props.portion}
                        rt={props.rt}
                        onDelete={onDelete}
                        onToggleTitles={() => setToggleBox(ToggleBox.Titles)}
                        onToggleTRL={() => setToggleBox(trlToggleBox)}
                    />
                </div>
                {toggleBox === ToggleBox.Titles && <TitlesEditor
                    portion={portion}
                    toggleTitles={setToggleBox}
                />}
                {toggleBox === ToggleBox.TRLIssue && <TRLIssueEditor
                    {...{ projectTrlIssue }} toggleTRLIssue={setToggleBox}
                />}
                {toggleBox === ToggleBox.TRLPublish && <TRLPublish
                    {...{ rt, portion }} onToggleTRL={() => setToggleBox(ToggleBox.TRLPublish)} 
                />}
            </div>
        )
    }
)

enum ToggleBox {
    Titles = 'titles',
    TRLIssue = 'trl-issue',
    TRLPublish = 'trl-publish',
}

const DragHandle: any = SortableHandle(() => 
    <RootConsumer>
        {rt => (
            <span className="portion-handle"
                data-toggle="tooltip" 
                title={t`Drag to reorder.`}>
                <i className="fas fa-bars"></i>
            </span>
        )}
    </RootConsumer>
)

interface IDownloadCountView {
    setDownloading: (downloading: boolean) => void,
}

// Show number of video blocks waiting to download
export const DownloadCountView: FC<IDownloadCountView> = ({ setDownloading }) => {
    const [downloadCount, setDownloadCount] = React.useState(VideoCache.videoCacheDownloader.downloadCount)

    // Update download count every 3 seconds.
    // Stop count display when it reaches 0.
    // Always shows for at least 3 seconds so that user can see that something happened when they
    // clicked download.
    useEffect(() => {
        const intervalId = setInterval(() => {
            const _downloadCount = VideoCache.videoCacheDownloader.downloadCount
            setDownloadCount(_downloadCount)
            if (VideoCacheDownloader.batchDownloadMode === true) {
                updatePortionDownloadedAt()
            }
            if (_downloadCount === 0) {
                setDownloading(false)
                VideoCacheDownloader.batchDownloadMode = false
                clearInterval(intervalId)
            }
        }, 3000) // update every 3 seconds

        return () => {
            // I suppose there's an edge case where the user changes from portions view
            // and then comes back. In any case, safest to clear batchDownloadMode
            VideoCacheDownloader.batchDownloadMode = false
            clearInterval(intervalId) // Clear the interval when the component is unmounted
        }
    }, [])

    return (
        <div className="download-count">
            <span
                className={`fas fa-cloud-download-alt download-count-icon`}
                data-toggle="tooltip"
                title={/* translator: important */ t`Number of video blocks waiting to download.`}
            />
            <span className="upload-count-value">{downloadCount}</span>
        </div>
    );
}

/** Start downloading videos for all passages in this portion, plus patches, notes, and large passage documents */
async function startPortionDownload(portion: Portion) {
    await requestPersistentStorage()
    for (let passage of portion.passages) {
        for (let passageVideo of passage.videos) {
            // Download the main video
            await VideoCache.videoCacheDownloader.implicitVideoDownload(passageVideo.url)
            log(`Downloaded main video: ${passageVideo.url}`)

            // Process patch videos for the current video
            for (let { patchVideo } of passageVideo.patchVideos(passage)) {
                // Download the patch video
                await VideoCache.videoCacheDownloader.implicitVideoDownload(patchVideo.url)
                log(`Downloaded patch video: ${patchVideo.url}`)

                // Process notes for the patch video
                await doNotesVideoDownload(patchVideo.notes, patchVideo.url)
            }

            // Process notes for the main video
            await doNotesVideoDownload(passageVideo.notes, passageVideo.url)

            await passageVideo.loadAllSketchPaths(passage)
        }
        // Download large passage documents too
        for (let document of passage.documents) {
            // Get the S3 URL
            const docS3Url = document.getS3Url()
            // Remove the s3: prefix
            const docUrl = document.getS3Url().replace(/^s3:/, '')
            // Attempt to download the document if docUrl is valid
            if (docUrl) {
                await VideoCache.videoCacheDownloader.implicitVideoDownload(docUrl)
                log(`Download large passage document: ${docUrl}`)
            }
        }
    }
} 

// Start downloading videos for all notes in this portion. Used by startPortionDownload()
async function doNotesVideoDownload(notes: any[], videoUrl: string) {
    for (let note of notes || []) {
        if (note.items && note.items.length !== 0) {
            for (let item of note.items) {
                if (item.url) {
                    await VideoCache.videoCacheDownloader.implicitVideoDownload(item.url)
                    log(`Download note video: ${item.url}`)
                }
            }
        }
    }
}

interface IPortionView {
    rt: Root,
    portion: Portion,
    onDelete: () => void,
    onToggleTitles: () => void,
    onToggleTRL: () => void,
}

const PortionView: FC<IPortionView> = observer((
    {
        rt, portion, onDelete, onToggleTitles, onToggleTRL
    }
) => {
    const [editingPortionName, setEditingPortionName] = useState(false)
    const [editingReferences, setEditingReferences] = useState(false)
    const [downloading, setDownloading] = useState(false)
    const portionRef = useRef<HTMLDivElement>(null)
    
    let { iAmTranslator, useMobileLayout, isTRLProject } = rt

    let portionIsSelected = rt.portion && portion && (rt.portion._id === portion._id)

    useEffect(() => {
        portionIsSelected && portionRef.current?.scrollIntoView({
            behavior: 'smooth',
            block: 'nearest',
            inline: 'nearest'
        })
    }, [portionIsSelected])

    if (editingPortionName) {
        return <PortionNameEditor {...{ rt, portion, setEditingPortionName }} />
    }

    async function downloadVideos() {
        VideoCacheDownloader.batchDownloadMode = true // set false when setDownloading(true)
        await startPortionDownload(portion)
        setDownloading(true)
    }

    return (
        <>
            <div title={t`Portion name (double-click to edit)`} className="portion-name portion-scroll-target"
                    data-id={portion.name} 
                    onDoubleClick={() => setEditingPortionName(true)}
                    ref={portionRef}>
                    {portion.name}
            </div>
            <div className="portion-notifications">
                <PortionNotificationList {...{ rt, portion, onToggleTitles, onToggleTRL }} />
            </div>
            { !useMobileLayout && (
                <PortionViewMenu {...{ rt, iAmTranslator, portion, onDelete, downloadVideos, setEditingReferences, setEditingPortionName, onToggleTitles, onToggleTRL }} />
            )}
            {isTRLProject && <div className="portion-notifications">
                <TrlNotificationList {...{ rt, portion, onToggleTitles, onToggleTRL }} />
            </div>}
            {downloading && <DownloadCountView {...{ setDownloading }} />}
        </> 
    )
})

interface IPortionViewMenu {
    rt: Root,
    portion: Portion,
    onDelete: () => void,
    downloadVideos: () => void,
    setEditingPortionName: (edit: boolean) => void,
    setEditingReferences: (edit: boolean) => void,
    iAmTranslator: boolean,
    onToggleTitles: () => void,
    onToggleTRL: () => void,
}

const PortionViewMenu: FC<IPortionViewMenu> = observer(({ rt, portion, onDelete, downloadVideos, 
            setEditingPortionName, setEditingReferences, iAmTranslator }) => 
{
    const canDelete = useUserCanDelete(portion)

    return (
        <div className="portion-menu">
            {iAmTranslator && <span className="portion-button portion-shadow-menu-item"
                    data-toggle="tooltip" 
                    data-id={'edit-' + portion.name} 
                    title={t`Rename portion.`} 
                    onClick={e => {
                        e.preventDefault()
                        setEditingPortionName(true)
                    }}>
                <i className="fas fa-fw fa-pencil-alt"></i>
            </span>}
            {canDelete && <span className="portion-button portion-shadow-menu-item"
                        data-toggle="tooltip" 
                        data-id={'delete-' + portion.name} 
                        title={t`Delete portion.`}    
                        onClick={e => {
                            e.preventDefault()
                            onDelete()
                        }}>
                <i className="fas fa-fw fa-trash-alt"></i>
            </span>}

            <span className="portion-button portion-download-button portion-shadow-menu-item"
                data-toggle="tooltip"
                data-id={'download-' + portion.name}
                title={t`Download videos in portion to temporary storage on this computer. This allows playing them later without an internet connection or waiting for a download.`}
                onClick={e => {
                    e.preventDefault()
                    downloadVideos()
                }}>
                <i className="fas fa-fw fa-download"></i>
            </span>

            {/* <ReferencesButton
                className='portion-button'
                tooltip={t`Edit references for this portion`}
                onClick={(e: any) => {
                    e.preventDefault()
                    setEditingReferences(true)
                }}
            /> */}
        </div>
    )
})

export default PortionListItem
